import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { 
  TrendingUp, 
  TrendingDown,
  MessageSquare,
  User,
  LogOut,
  RefreshCw,
  BarChart3,
  Gem,
  Droplet,
  Flame,
  Lock,
  Crown,
  Star,
  Plus,
  X,
  Sparkles,
  ExternalLink,
  Award,
  Bitcoin
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("token");

const LOGO_URL = "/doslions-logo.png";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [stocks, setStocks] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [crypto, setCrypto] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);

  const fetchData = async () => {
    try {
      const [stocksRes, commoditiesRes, cryptoRes, watchlistRes] = await Promise.all([
        axios.get(`${API}/stocks`),
        axios.get(`${API}/commodities`),
        axios.get(`${API}/crypto`),
        axios.get(`${API}/watchlist`, { headers: { Authorization: `Bearer ${getToken()}` }}).catch(() => ({ data: { watchlist: [] }}))
      ]);
      setStocks(stocksRes.data.stocks);
      setCommodities(commoditiesRes.data.commodities);
      setCrypto(cryptoRes.data.crypto || []);
      setWatchlist(watchlistRes.data.watchlist || []);
    } catch (error) {
      toast.error("Failed to fetch market data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
    toast.success("Data refreshed");
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  const addToWatchlist = async (symbol, type) => {
    try {
      await axios.post(`${API}/watchlist`, { symbol, type }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success(`${symbol} added to watchlist`);
      fetchData();
    } catch (error) {
      toast.error("Failed to add to watchlist");
    }
  };

  const removeFromWatchlist = async (symbol) => {
    try {
      await axios.delete(`${API}/watchlist/${symbol}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success(`${symbol} removed from watchlist`);
      fetchData();
    } catch (error) {
      toast.error("Failed to remove from watchlist");
    }
  };

  const isInWatchlist = (symbol) => {
    return watchlist.some(item => item.symbol === symbol);
  };

  const analyzePortfolio = async () => {
    if (!user?.is_subscribed) {
      toast.error("Subscribe to Pro for AI analysis");
      navigate("/subscription");
      return;
    }
    
    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/ai/analyze-portfolio`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setAnalysis(response.data);
      toast.success("Analysis complete!");
    } catch (error) {
      toast.error("Failed to analyze portfolio");
    } finally {
      setAnalyzing(false);
    }
  };

  const getCommodityIcon = (symbol) => {
    switch (symbol) {
      case "GOLD": case "SILVER": case "PLATINUM": return <Gem className="w-4 h-4" />;
      case "OIL": return <Droplet className="w-4 h-4" />;
      case "NATGAS": return <Flame className="w-4 h-4" />;
      default: return <BarChart3 className="w-4 h-4" />;
    }
  };

  const isPositive = (change) => {
    return change && (change.startsWith('+') || (!change.startsWith('-') && parseFloat(change) > 0));
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] pb-20 md:pb-0">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black/80 backdrop-blur-xl border-b border-amber-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="ECOLIONS" className="w-10 h-10 object-contain" />
              <div className="hidden sm:block">
                <span className="font-bold text-xl tracking-tight text-amber-500" style={{ fontFamily: 'Barlow Condensed' }}>
                  ECOLIONS
                </span>
                <span className="font-bold text-xl tracking-tight text-white ml-1" style={{ fontFamily: 'Barlow Condensed' }}>
                  AI MARKETS
                </span>
              </div>
            </div>
            
            <nav className="hidden md:flex items-center gap-1">
              <Button 
                variant="ghost" 
                onClick={() => navigate("/dashboard")}
                className="text-amber-500"
                data-testid="nav-dashboard"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Markets
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => navigate("/chat")}
                className="text-zinc-400 hover:text-amber-500"
                data-testid="nav-chat"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                LIONAI
                {!user?.is_subscribed && <Lock className="w-3 h-3 ml-1 text-zinc-600" />}
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => navigate("/results")}
                className="text-zinc-400 hover:text-amber-500"
                data-testid="nav-results"
              >
                <Award className="w-4 h-4 mr-2" />
                Results
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => navigate("/subscription")}
                className="text-zinc-400 hover:text-amber-500"
                data-testid="nav-subscription"
              >
                Subscription
              </Button>
            </nav>

            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={handleRefresh}
                disabled={refreshing}
                className="text-zinc-400 hover:text-amber-500"
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate("/profile")}
                className="text-zinc-400 hover:text-amber-500"
                data-testid="profile-btn"
              >
                <User className="w-4 h-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={handleLogout}
                className="text-zinc-400 hover:text-amber-500"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Daily Insight - Quick Win */}
        <div className="bg-gradient-to-r from-emerald-500/10 to-transparent border border-emerald-500/30 p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-6 h-6 text-emerald-500" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-emerald-500 uppercase tracking-wider font-bold mb-1">Today's LIONAI Insight</p>
              <p className="text-zinc-300 mb-2">
                Gold is showing strong support at $2,040. With inflation concerns rising, watch for potential breakout above $2,050 resistance.
              </p>
              <div className="flex items-center gap-4">
                <Button 
                  size="sm"
                  onClick={() => navigate("/chat?query=Tell me more about gold price action today")}
                  className="bg-emerald-500 hover:bg-emerald-600 text-black text-xs"
                >
                  Ask LIONAI More
                </Button>
                <p className="text-xs text-zinc-500">Updated 2 hours ago</p>
              </div>
            </div>
          </div>
        </div>

        {/* Welcome Banner */}
        <div className="bg-[#111111] border border-zinc-800 p-6 mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-xs tracking-[0.3em] text-zinc-500 font-bold uppercase mb-1">
                Welcome back
              </p>
              <h1 
                className="text-3xl font-bold tracking-tight uppercase"
                style={{ fontFamily: 'Barlow Condensed' }}
              >
                {user?.name || 'Trader'}
              </h1>
              {user?.is_subscribed && (
                <div className="flex items-center gap-2 mt-2">
                  <Crown className="w-4 h-4 text-amber-500" />
                  <span className="text-xs text-amber-500 font-bold uppercase tracking-wider">Pro Member</span>
                </div>
              )}
            </div>
            {!user?.is_subscribed && (
              <Button 
                onClick={() => navigate("/subscription")}
                className="bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider shadow-[0_0_20px_rgba(245,158,11,0.3)]"
                data-testid="upgrade-btn"
              >
                <Crown className="w-4 h-4 mr-2" />
                Unlock AI - $3/mo
              </Button>
            )}
          </div>
        </div>

        {/* Watchlist Section */}
        {watchlist.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold tracking-tight uppercase flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                <Star className="w-5 h-5 text-amber-500" />
                Your Watchlist
              </h2>
              {user?.is_subscribed && (
                <Button 
                  onClick={analyzePortfolio}
                  disabled={analyzing}
                  className="bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider text-xs"
                  data-testid="analyze-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  {analyzing ? "Analyzing..." : "AI Analysis"}
                </Button>
              )}
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {watchlist.map((item) => (
                <div 
                  key={item.symbol}
                  className="bg-[#111111] border border-amber-500/30 p-4 relative group"
                >
                  <button
                    onClick={() => removeFromWatchlist(item.symbol)}
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-zinc-500 hover:text-red-500"
                    data-testid={`remove-${item.symbol}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <p className="text-xs text-amber-500 font-bold tracking-wider">{item.symbol}</p>
                  {item.price_data && (
                    <>
                      <p className="text-lg font-bold font-data mt-1">${item.price_data.price}</p>
                      <p className={`text-xs font-data ${isPositive(item.price_data.change) ? 'text-emerald-500' : 'text-red-500'}`}>
                        {item.price_data.change_percent}
                      </p>
                    </>
                  )}
                </div>
              ))}
            </div>

            {/* AI Analysis Results */}
            {analysis && (
              <div className="mt-4 bg-amber-500/5 border border-amber-500/30 p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  <h3 className="font-bold text-amber-500">AI Portfolio Analysis</h3>
                </div>
                <p className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">
                  {analysis.analysis}
                </p>
                <p className="text-xs text-zinc-500 mt-4">
                  Based on your {analysis.user_profile?.risk} risk profile and {analysis.user_profile?.goals} goals
                </p>
              </div>
            )}
          </div>
        )}

        {/* Market Data */}
        <Tabs defaultValue="stocks" className="space-y-6">
          <TabsList className="bg-[#111111] border border-zinc-800 p-1">
            <TabsTrigger 
              value="stocks" 
              className="data-[state=active]:bg-amber-500 data-[state=active]:text-black uppercase tracking-wider text-xs font-bold"
              data-testid="stocks-tab"
            >
              Stocks
            </TabsTrigger>
            <TabsTrigger 
              value="crypto" 
              className="data-[state=active]:bg-amber-500 data-[state=active]:text-black uppercase tracking-wider text-xs font-bold"
              data-testid="crypto-tab"
            >
              Crypto
            </TabsTrigger>
            <TabsTrigger 
              value="commodities" 
              className="data-[state=active]:bg-amber-500 data-[state=active]:text-black uppercase tracking-wider text-xs font-bold"
              data-testid="commodities-tab"
            >
              Commodities
            </TabsTrigger>
          </TabsList>

          <TabsContent value="stocks" className="space-y-4">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="bg-[#111111] border border-zinc-800 p-6 animate-pulse">
                    <div className="h-4 bg-zinc-800 rounded w-16 mb-3"></div>
                    <div className="h-8 bg-zinc-800 rounded w-24 mb-2"></div>
                    <div className="h-4 bg-zinc-800 rounded w-20"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {stocks.map((stock) => (
                  <div 
                    key={stock.symbol}
                    className="bg-[#111111] border border-zinc-800 p-6 card-hover group relative"
                    data-testid={`stock-card-${stock.symbol}`}
                  >
                    <button
                      onClick={() => isInWatchlist(stock.symbol) 
                        ? removeFromWatchlist(stock.symbol) 
                        : addToWatchlist(stock.symbol, 'stock')
                      }
                      className={`absolute top-3 right-3 transition-all ${
                        isInWatchlist(stock.symbol) 
                          ? 'text-amber-500' 
                          : 'text-zinc-600 opacity-0 group-hover:opacity-100'
                      }`}
                      data-testid={`watchlist-${stock.symbol}`}
                    >
                      {isInWatchlist(stock.symbol) ? <Star className="w-5 h-5 fill-amber-500" /> : <Plus className="w-5 h-5" />}
                    </button>
                    
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-xs tracking-wider text-zinc-500 font-bold">
                        {stock.symbol}
                      </p>
                      {isPositive(stock.change) ? (
                        <TrendingUp className="w-4 h-4 text-emerald-500" />
                      ) : (
                        <TrendingDown className="w-4 h-4 text-red-500" />
                      )}
                    </div>
                    <p className="text-sm text-zinc-400 mb-2 truncate">{stock.name}</p>
                    <p className="text-2xl font-bold font-data">
                      ${stock.price}
                    </p>
                    <p className={`text-sm font-data mt-2 ${isPositive(stock.change) ? 'text-emerald-500' : 'text-red-500'}`}>
                      {stock.change} ({stock.change_percent})
                    </p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="crypto" className="space-y-4">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-[#111111] border border-zinc-800 p-6 animate-pulse">
                    <div className="h-4 bg-zinc-800 rounded w-16 mb-3"></div>
                    <div className="h-8 bg-zinc-800 rounded w-24 mb-2"></div>
                    <div className="h-4 bg-zinc-800 rounded w-20"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {crypto.map((coin) => (
                  <div 
                    key={coin.id}
                    className="bg-[#111111] border border-zinc-800 p-6 card-hover group relative"
                    data-testid={`crypto-card-${coin.symbol}`}
                  >
                    <button
                      onClick={() => isInWatchlist(coin.symbol) 
                        ? removeFromWatchlist(coin.symbol) 
                        : addToWatchlist(coin.symbol, 'crypto')
                      }
                      className={`absolute top-3 right-3 transition-all ${
                        isInWatchlist(coin.symbol) 
                          ? 'text-amber-500' 
                          : 'text-zinc-600 opacity-0 group-hover:opacity-100'
                      }`}
                      data-testid={`watchlist-${coin.symbol}`}
                    >
                      {isInWatchlist(coin.symbol) ? <Star className="w-5 h-5 fill-amber-500" /> : <Plus className="w-5 h-5" />}
                    </button>
                    
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {coin.image ? (
                          <img src={coin.image} alt={coin.name} className="w-8 h-8 rounded-full" />
                        ) : (
                          <div className="w-8 h-8 bg-amber-500/10 flex items-center justify-center text-amber-500">
                            <Bitcoin className="w-4 h-4" />
                          </div>
                        )}
                        <p className="text-xs tracking-wider text-zinc-500 font-bold">
                          {coin.symbol}
                        </p>
                      </div>
                      {isPositive(coin.change_percent) ? (
                        <TrendingUp className="w-4 h-4 text-emerald-500" />
                      ) : (
                        <TrendingDown className="w-4 h-4 text-red-500" />
                      )}
                    </div>
                    <p className="text-sm text-zinc-400 mb-2">{coin.name}</p>
                    <p className="text-2xl font-bold font-data">
                      ${coin.price}
                    </p>
                    <p className={`text-sm font-data mt-2 ${isPositive(coin.change_percent) ? 'text-emerald-500' : 'text-red-500'}`}>
                      {coin.change_percent}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="commodities" className="space-y-4">
            {/* Commodities - Coming Soon with Premium */}
            <div className="bg-[#111111] border border-amber-500/30 p-8 text-center">
              <div className="w-16 h-16 bg-amber-500/20 mx-auto flex items-center justify-center mb-4">
                <Gem className="w-8 h-8 text-amber-500" />
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                COMMODITIES <span className="text-amber-500">COMING SOON</span>
              </h3>
              <p className="text-zinc-400 mb-4 max-w-md mx-auto">
                Live commodity prices (Gold, Silver, Oil, Natural Gas) will be available with Premium subscription.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-2xl mx-auto mb-6">
                {commodities.map((commodity) => (
                  <div 
                    key={commodity.symbol}
                    className="bg-[#0A0A0A] border border-zinc-800 p-4"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 bg-amber-500/10 flex items-center justify-center text-amber-500">
                        {getCommodityIcon(commodity.symbol)}
                      </div>
                      <p className="text-xs tracking-wider text-zinc-500 font-bold">
                        {commodity.symbol}
                      </p>
                    </div>
                    <p className="text-sm text-zinc-400">{commodity.name}</p>
                    <p className="text-xs text-zinc-600 mt-1">/{commodity.unit}</p>
                  </div>
                ))}
              </div>
              <Button 
                onClick={() => navigate("/subscription")}
                className="bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider"
              >
                <Crown className="w-4 h-4 mr-2" />
                Unlock with Premium
              </Button>
            </div>
          </TabsContent>
        </Tabs>

        {/* AI Access Card */}
        <div className="mt-8 bg-[#111111] border border-amber-500/30 p-6 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 to-transparent"></div>
          <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 
                className="text-xl font-bold tracking-tight uppercase mb-2"
                style={{ fontFamily: 'Barlow Condensed' }}
              >
                <MessageSquare className="w-5 h-5 inline mr-2 text-amber-500" />
                LIONAI Market Analyst
              </h2>
              <p className="text-sm text-zinc-400">
                {user?.is_subscribed 
                  ? "Ask LIONAI anything about markets, get personalized insights"
                  : "Unlock LIONAI to ask unlimited questions about any stock or commodity"
                }
              </p>
            </div>
            {user?.is_subscribed ? (
              <Button 
                onClick={() => navigate("/chat")}
                className="bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider"
                data-testid="open-chat-btn"
              >
                Open AI Chat
              </Button>
            ) : (
              <Button 
                onClick={() => navigate("/subscription")}
                className="bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider shadow-[0_0_20px_rgba(245,158,11,0.3)]"
                data-testid="unlock-ai-btn"
              >
                <Lock className="w-4 h-4 mr-2" />
                Unlock for $3/mo
              </Button>
            )}
          </div>
        </div>
      </main>

      {/* Mobile Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-black/90 backdrop-blur-xl border-t border-amber-500/20">
        <div className="flex items-center justify-around py-3">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate("/dashboard")}
            className="flex-col h-auto py-2 text-amber-500"
            data-testid="mobile-nav-markets"
          >
            <BarChart3 className="w-5 h-5 mb-1" />
            <span className="text-xs">Markets</span>
          </Button>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate("/chat")}
            className="flex-col h-auto py-2 text-zinc-500"
            data-testid="mobile-nav-chat"
          >
            <MessageSquare className="w-5 h-5 mb-1" />
            <span className="text-xs">AI Chat</span>
          </Button>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate("/profile")}
            className="flex-col h-auto py-2 text-zinc-500"
            data-testid="mobile-nav-profile"
          >
            <User className="w-5 h-5 mb-1" />
            <span className="text-xs">Profile</span>
          </Button>
        </div>
      </nav>
    </div>
  );
}
